import React, { useState } from "react";
import { Link as RouteLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button, Grid, Stack } from "@mui/material";
import styled, { useTheme } from "styled-components";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import fetchStatus from "src/utils/api/fetch-status";
import Picture from "src/components/picture";
import { useAuth } from "src/components/providers/auth-context";
import { useFeature } from "src/components/providers/feature-flags";
import ContainedTextField from "src/components/form-fields/contained-text-field";
import request from "src/utils/request";
import { TEAM_SHARE } from "src/utils/feaure-flag-constants";
import TeamInvitedCard from "src/scenes/teams/team/team-invited-card";
import TeamMemberInviteModal from "src/scenes/teams/team/team-member-invite-modal";
import TeamUnregisteredCard from "src/scenes/teams/team/team-unregistered-card";
import { useOrg } from "src/components/providers/org-provider";
import OnlyOwnerTooltip from "./only-owner-tooltip";
import TeamDelete from "./team-delete";
import TeamLeave from "./team-leave";
import { useTeam } from "./team-provider";
import TeamMemberModal from "./team-member-modal";
import TeamMemberCard from "./team-member-card";
import TeamMemberAddCard from "./team-member-add-card";
import TeamPrivacySetting from "./team-privacy-setting";
import TeamJoin from "./team-join";
import useTeamAction from "../hooks/use-team-action";

const TeamMembersTitle = styled.h2`
    font-size: 18px;
`;

const TeamCreatorText = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const TeamCreatorName = styled.h3`
    font-size: 12px;
    font-weight: 700;
    margin: 0;
    padding: 0;
`;

const Share = styled(Button)`
    font-size: 12px;
    font-weight: 300;
    text-transform: none;
    padding: 0;
    color: ${({ theme }) => theme.palette.text.secondary};
    &:hover {
        background-color: ${({ theme }) => theme.palette.background.default};
    }
`;

const MissionsHistoryTeamsLink = styled(RouteLink)`
    font-size: 12px;
    font-weight: 300;
    line-height: 20px;
    font-style: normal;
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const Team = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState();
    const isTeamSharingFeatureEnabled = useFeature(TEAM_SHARE);
    const { user } = useAuth();
    const { teamId } = useParams();
    const teamApi = useTeam();
    const theme = useTheme();
    const { org } = useOrg();

    const updateTeamAction = (type) =>
        useTeamAction(
            (value) =>
                request(`/api/teams/${teamId}`, {
                    method: "POST",
                    body: JSON.stringify({ [type]: value }),
                }),
            () => {}
        );

    const handleCardClick = (member) => {
        setSelectedMember(member);
        setIsModalOpen(true);
    };

    const handleInviteCardClick = (member) => {
        setSelectedMember(member);
        setIsInvitationModalOpen(true);
    };

    const renderMembers = () => {
        let data;
        if (!teamApi || teamApi.status === fetchStatus.PENDING) {
            data = Array(3).fill(0);
        } else {
            data = (teamApi.data && teamApi.data.members) || [];
        }
        return data.map((member) => (
            <Grid item justifyContent="space-evenly">
                <ShowWithSkeleton fetchAction={teamApi}>
                    <TeamMemberCard member={member} onClick={handleCardClick} isInteractive />
                </ShowWithSkeleton>
            </Grid>
        ));
    };

    const renderUnregisteredMembers = () => {
        let data;
        if (!teamApi || teamApi.status === fetchStatus.PENDING) {
            data = Array(3).fill(0);
        } else {
            data = (teamApi.data && teamApi.data.tempMembers) || [];
        }
        return data.map((member) => (
            <Grid key={member.email} item justifyContent="space-evenly">
                <ShowWithSkeleton fetchAction={teamApi}>
                    <TeamUnregisteredCard member={member} onClick={handleCardClick} isInteractive />
                </ShowWithSkeleton>
            </Grid>
        ));
    };

    const renderInvitedMembers = () => {
        let invitedMembers;
        let tempMembers;
        if (!teamApi || teamApi.status === fetchStatus.PENDING) {
            invitedMembers = Array(3).fill(0);
        } else {
            invitedMembers = (teamApi.data && teamApi.data.pendingMembers) || [];
            tempMembers = (teamApi.data && teamApi.data.tempMembers) || [];
        }
        if (!Array.isArray(invitedMembers)) {
            invitedMembers = [];
        }
        if (!Array.isArray(tempMembers)) {
            tempMembers = [];
        }
        return invitedMembers
            .filter((member) => !tempMembers.some((invited) => invited.email === member.email))
            .map((invite) => (
                <Grid item justifyContent="space-evenly" key={invite.email}>
                    <ShowWithSkeleton fetchAction={teamApi}>
                        <TeamInvitedCard
                            invite={invite}
                            onClick={handleInviteCardClick}
                            isInteractive
                        />
                    </ShowWithSkeleton>
                </Grid>
            ));
    };

    return (
        <>
            <Helmet>
                {teamApi.data && teamApi.data.name && (
                    <title>Adadot - {teamApi.data && teamApi.data.name}</title>
                )}
            </Helmet>
            <Grid
                style={{ marginTop: "2vh", marginBottom: "50px" }}
                container
                direction="row"
                spacing={5}
            >
                <Grid item container direction="row" justifyContent="space-between">
                    <Grid item container direction="column" style={{ width: "auto" }}>
                        <Stack direction="row" spacing={2}>
                            <ShowWithSkeleton
                                fetchAction={teamApi}
                                height={124}
                                width={124}
                                withKids={false}
                            >
                                <Picture
                                    picture={teamApi.data && teamApi.data.picture}
                                    requestUrl={`/api/teams/${teamId}/picture`}
                                />
                            </ShowWithSkeleton>
                            <Stack
                                direction="column"
                                alignItems="baseline"
                                justifyContent="flex-start"
                                spacing={1}
                            >
                                <ContainedTextField
                                    initialValue={teamApi.data && teamApi.data.name}
                                    refetch={teamApi.refetch}
                                    placeholderText="Enter your team's name"
                                    requestFormatter={updateTeamAction("name")}
                                />
                                <ContainedTextField
                                    fontSize={12}
                                    defaultColor={theme.palette.primary.main}
                                    initialValue={teamApi.data && teamApi.data.description}
                                    refetch={teamApi.refetch}
                                    placeholderText="Enter your team's description"
                                    requestFormatter={updateTeamAction("description")}
                                />
                                {teamApi.data && teamApi.data.creator && (
                                    <Stack direction="row">
                                        <TeamCreatorText>Created by</TeamCreatorText>&nbsp;
                                        <TeamCreatorName>
                                            {teamApi.data.creator.displayName ||
                                                teamApi.data.creator.email}
                                        </TeamCreatorName>
                                    </Stack>
                                )}
                                <OnlyOwnerTooltip>
                                    <TeamDelete team={teamApi.data} />
                                </OnlyOwnerTooltip>
                                <TeamLeave team={teamApi.data} />
                                <TeamJoin team={teamApi.data} />
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid
                        item
                        container
                        direction="column"
                        style={{ width: "auto", minWidth: "200px" }}
                        justifyContent="center"
                    >
                        <Stack spacing={2} alignItems="center" justifyContent="center">
                            {teamApi &&
                            teamApi.data &&
                            teamApi.data.membersCount >= (org.minimumTeamMembers || 3) ? (
                                <>
                                    <Button
                                        as={RouteLink}
                                        to={`/teams/${teamId}/dashboard`}
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        style={{
                                            textDecoration: "auto",
                                            textAlign: "center",
                                        }}
                                    >
                                        dashboard
                                    </Button>
                                    <MissionsHistoryTeamsLink
                                        teamId={teamId}
                                        team={teamApi.data}
                                        to={`/teams/${teamId}/missions-history`}
                                    >
                                        Missions History
                                    </MissionsHistoryTeamsLink>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled
                                        style={{
                                            textDecoration: "auto",
                                            textAlign: "center",
                                        }}
                                    >
                                        dashboard
                                    </Button>
                                    <TeamCreatorText>
                                        Add {org.minimumTeamMembers || 3} or more users to enable
                                        Dashboard
                                    </TeamCreatorText>
                                </>
                            )}
                            {isTeamSharingFeatureEnabled && (
                                <>
                                    <OnlyOwnerTooltip>
                                        <TeamPrivacySetting
                                            disabled={
                                                !teamApi.data || teamApi.data.owner.id !== user.id
                                            }
                                        />
                                    </OnlyOwnerTooltip>
                                    <OnlyOwnerTooltip>
                                        <Share
                                            disabled={
                                                !teamApi.data || teamApi.data.owner.id !== user.id
                                            }
                                        >
                                            Share team stats
                                        </Share>
                                    </OnlyOwnerTooltip>
                                </>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <Grid item container>
                    <Stack>
                        <TeamMembersTitle>Team Members</TeamMembersTitle>
                        <Grid container spacing={2}>
                            <Grid item>
                                <TeamMemberAddCard team={teamApi.data} user={user} />
                            </Grid>
                            {renderMembers()}
                            {renderUnregisteredMembers()}
                            {renderInvitedMembers()}
                        </Grid>
                    </Stack>
                </Grid>
            </Grid>
            <TeamMemberInviteModal
                open={isInvitationModalOpen}
                handleClose={() => setIsInvitationModalOpen(false)}
                team={teamApi.data}
                selectedMember={selectedMember}
            />
            <TeamMemberModal
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                team={teamApi.data}
                selectedMember={selectedMember}
            />
        </>
    );
};

export default Team;
